/**
 * Mint-auth credential carrying (TI-974).
 *
 * VSS's `POST /livekit/token` accepts staged credentials; this module makes
 * the room CARRY them when present:
 *   - `Authorization: Bearer <UM access JWT>` — posted by the embedding parent
 *     (interviews-ui) via `{ type: "ti:auth", accessToken: "<string>" }`,
 *     origin-validated against the SAME allow-list as the `ti:session`
 *     message (see `isAllowedParentOrigin` in correlation.js).
 *   - `X-TI-Invite-Token: <token>` — read from the join URL's `inviteToken`
 *     query param. Only the invite token may come from the URL; an access
 *     token is NEVER read from the URL (URLs leak via history/logs).
 *
 * Fail-open contract: when neither credential is available,
 * `getMintAuthHeaders()` returns `{}` and the outgoing mint request stays
 * byte-identical to the pre-TI-974 request — no new headers, no errors.
 * Token VALUES are never logged.
 */
import { isAllowedParentOrigin } from "./correlation";

export const AUTHORIZATION_HEADER = "Authorization";
export const INVITE_TOKEN_HEADER = "X-TI-Invite-Token";

/**
 * Module-level UM access JWT. Set only via a validated `ti:auth` postMessage
 * (or explicitly through this setter, e.g. in tests). Blank/non-string clears.
 */
let accessToken = null;

export function setMintAccessToken(token) {
  const trimmed = typeof token === "string" ? token.trim() : "";
  accessToken = trimmed || null;
}

/**
 * Reads the invite token from a query string (defaults to the live
 * `window.location`). Only the `inviteToken` param is honored. Returns `null`
 * when absent/blank; never throws (fail-open).
 *
 * @param {string} [search] e.g. `?inviteToken=abc`
 */
export function readInviteTokenFromQuery(search) {
  try {
    const qs =
      search !== undefined
        ? search
        : typeof window !== "undefined"
        ? window.location.search
        : "";
    const params = new URLSearchParams(qs);
    const raw = params.get("inviteToken");
    const trimmed = raw ? raw.trim() : "";
    return trimmed || null;
  } catch (err) {
    console.warn("[mintAuth] could not parse inviteToken from query", err);
    return null;
  }
}

/**
 * Credentials to attach to the VSS token mint. `{}` when nothing is held
 * (fail-open — the request must look exactly like today's); `Authorization`
 * when the parent posted an access token; `X-TI-Invite-Token` when the join
 * URL carries one; both when both exist.
 */
export function getMintAuthHeaders() {
  const headers = {};
  if (accessToken) {
    headers[AUTHORIZATION_HEADER] = `Bearer ${accessToken}`;
  }
  const inviteToken = readInviteTokenFromQuery();
  if (inviteToken) {
    headers[INVITE_TOKEN_HEADER] = inviteToken;
  }
  return headers;
}

/** Teardown for the installed listener; also the idempotency latch. */
let teardownMintAuthListener = null;

/**
 * Message contract (parent → embedded room):
 *   { type: "ti:auth", accessToken: "<UM access JWT>" }
 *
 * Validates `event.origin` against the shared parent-origin allow-list and the
 * message shape before storing the token. Arbitrary-origin or malformed
 * messages are ignored; the token value is never logged. Idempotent — a second
 * install returns the existing teardown without adding a duplicate listener.
 */
export function installMintAuthListener() {
  if (typeof window === "undefined") return () => {};
  if (teardownMintAuthListener) return teardownMintAuthListener;

  const onMessage = (event) => {
    if (!isAllowedParentOrigin(event.origin)) {
      // Silently ignore unknown origins — postMessage is a public surface.
      return;
    }
    const data = event.data;
    if (!data || typeof data !== "object" || data.type !== "ti:auth") {
      return;
    }
    if (typeof data.accessToken !== "string" || !data.accessToken.trim()) {
      console.warn("[mintAuth] ti:auth message had no accessToken");
      return;
    }
    setMintAccessToken(data.accessToken);
  };

  window.addEventListener("message", onMessage);
  teardownMintAuthListener = () => {
    window.removeEventListener("message", onMessage);
    teardownMintAuthListener = null;
  };
  return teardownMintAuthListener;
}
